import React, { useState, useEffect } from "react";
import { db } from "@/api/db";
import { motion, AnimatePresence } from "framer-motion";
import VocationalIntro from "@/components/vocational/VocationalIntro";
import VocationalQuestion from "@/components/vocational/VocationalQuestion";
import VocationalResult from "@/components/vocational/VocationalResult";
import { QUESTIONS } from "@/components/vocational/vocationalData";
import { calculateResult } from "@/components/vocational/vocationalAlgorithm";

export default function VocationalTest() {
  const [stage, setStage] = useState("loading"); // loading | intro | test | result
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [user, setUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [completedAt, setCompletedAt] = useState(null);

  useEffect(() => {
    // Safety timeout: se auth demorar demais, vai direto pra intro
    const timeout = setTimeout(() => setStage((s) => s === "loading" ? "intro" : s), 5000);

    db.auth.me().then(async (u) => {
      setUser(u);
      if (u) {
        const profiles = await db.entities.UserProfile.filter({ user_email: u.email }, "-created_date", 1);
        const p = profiles[0];
        if (p?.vocational_completed && p?.vocational_answers) {
          const computed = calculateResult(p.vocational_answers);
          setResult(computed);
          setAnswers(p.vocational_answers);

          // Backfill: testes antigos não tinham vocational_completed_at
          let savedAt = p.vocational_completed_at;
          if (!savedAt) {
            savedAt = new Date().toISOString();
            db.entities.UserProfile.update(p.id, { vocational_completed_at: savedAt }).catch(() => {});
          }
          setCompletedAt(savedAt);

          setStage("result");
          return;
        }
      }
      setStage("intro");
    }).catch(() => setStage("intro"))
      .finally(() => clearTimeout(timeout));
  }, []);

  function startTest() {
    setStage("test");
    setCurrentQ(0);
    setAnswers({});
  }

  function handleAnswer(questionId, value) {
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    if (currentQ < QUESTIONS.length - 1) {
      setTimeout(() => setCurrentQ(c => c + 1), 300);
    } else {
      finishTest(newAnswers);
    }
  }

  async function finishTest(finalAnswers) {
    const computed = calculateResult(finalAnswers);
    setResult(computed);
    setStage("result");

    const now = new Date().toISOString();
    setCompletedAt(now);

    if (user) {
      setSaving(true);
      try {
        const existing = await db.entities.UserProfile.filter({ user_email: user.email }, "-created_date", 1);
        const coreData = {
          user_email: user.email,
          vocational_completed: true,
          vocational_result: computed.topProfessions[0]?.name || "",
          vocational_answers: finalAnswers,
        };
        const id = existing[0]?.id;
        if (id) {
          await db.entities.UserProfile.update(id, coreData);
        } else {
          await db.entities.UserProfile.create(coreData);
        }
        // Timestamp separado — só funciona se a migração 018 já rodou
        if (id) {
          db.entities.UserProfile.update(id, { vocational_completed_at: now }).catch(() => {});
        }
      } catch {
        // Garante que o resultado aparece mesmo se o save falhar
      } finally {
        setSaving(false);
      }
    }
  }

  function handleRetake() {
    setStage("intro");
    setAnswers({});
    setResult(null);
  }

  if (stage === "loading") {
    return (
      <div className="flex items-center justify-center py-20">
        <div
          className="w-6 h-6 border-2 rounded-full animate-spin"
          style={{ borderColor: "#E5E9EA", borderTopColor: "#00DCA4" }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <AnimatePresence mode="wait">
        {stage === "intro" && (
          <VocationalIntro key="intro" onStart={startTest} />
        )}
        {stage === "test" && (
          <VocationalQuestion
            key="test"
            question={QUESTIONS[currentQ]}
            questionIndex={currentQ}
            total={QUESTIONS.length}
            onAnswer={handleAnswer}
            selectedAnswer={answers[QUESTIONS[currentQ]?.id]}
          />
        )}
        {stage === "result" && result && (
          <VocationalResult
            key="result"
            result={result}
            user={user}
            saving={saving}
            completedAt={completedAt}
            onRetake={handleRetake}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
